import { type ReactNode, useState } from 'react';

import {
  type ColorToken,
  CountPill,
  HorizontalMenuTabs,
  MetricCard,
  PageHeader,
  VerticalBarChart,
  type VerticalBarChartBar,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import {
  type ActivitySeries,
  type CoachCounts,
  NO_DATA_FILTER_VALUE,
  type OverviewResponse,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import { Box, type SxProps, type Theme, Tooltip, Typography } from '@mui/material';

import { type AnalyticsSection } from './analytics.anchors';
import { percentOf, programLabel, stageLabel } from './analytics.labels';
import {
  COACHING_COACH,
  MY_STUDENTS_ONLY,
  OVERVIEW_WITH_COACHES,
  OVERVIEW_WITHOUT_COACHES,
} from './analytics.layouts';
import {
  dividerSx,
  mutedTextSx,
  pctChipSx,
  pctChipTextSx,
  rowLabelSx,
  tallCardSx,
  titleSx,
  withClickable,
} from './analytics.styles';
import { AnalyticsCard, CardInfoIcon, titleRowSx, ViewDataTrigger } from './AnalyticsCard';
import { AnalyticsCardGrid } from './AnalyticsCardGrid';
import { type ActivityLine, ActivityLegend, ActivityLineChart } from './EngagementCards';
import { useAgentContext, useMyCoachingAgentContext } from './OverviewCards.agentContext';
import { type SegmentFilter } from './StudentDataModal';
import { useStudentData } from './useStudentData';
import { COLUMNS, COMMON_COLUMNS } from './viewData.columns';

const { coachingAnalytics } = appStrings.modules;
const { hover } = coachingAnalytics;

type BreakdownRow = Readonly<{ id: string; label: string; value: number }>;

type CoverageRow = Readonly<{
  id: string;
  label: string;
  count: number;
  pct: number;
  segment: SegmentFilter;
}>;

const EngagementMetricCard = ({
  overview,
  onClick,
}: Readonly<{ overview: OverviewResponse; onClick?: () => void }>) => (
  <MetricCard
    id="coaching-analytics-metric-engagement"
    label={coachingAnalytics.metrics.engagement}
    value={overview.recently_active.count}
    action={
      <CardInfoIcon
        info={[coachingAnalytics.metrics.engagementInfo]}
        title={coachingAnalytics.metrics.engagement}
      />
    }
    onClick={onClick}
    footer={
      <Typography variant="text-sm-regular" sx={mutedTextSx}>
        {coachingAnalytics.metrics.engagementSubtitle}
      </Typography>
    }
  />
);

const CoachesMetricCard = ({
  counts,
  onClick,
}: Readonly<{ counts: CoachCounts; onClick?: () => void }>) => (
  <MetricCard
    id="coaching-analytics-metric-coaches"
    label={coachingAnalytics.metrics.coaches}
    value={counts.total}
    action={
      <CardInfoIcon
        info={[coachingAnalytics.metrics.coachesInfo]}
        title={coachingAnalytics.metrics.coaches}
      />
    }
    onClick={onClick}
    footer={
      <Box sx={pctChipSx}>
        <Typography variant="text-xs-medium" sx={pctChipTextSx}>
          {coachingAnalytics.metrics.backrsCoaches(counts.backrs)}
        </Typography>
      </Box>
    }
  />
);

const coverageRowsOf = (overview: OverviewResponse): readonly CoverageRow[] => {
  const { assessmentCoverage } = coachingAnalytics;
  const { completions } = overview;

  return [
    {
      id: 'blueprint',
      label: assessmentCoverage.blueprint,
      count: completions.blueprint.count,
      pct: completions.blueprint.percentage,
      segment: {
        label: assessmentCoverage.blueprint,
        where: [{ field: 'blueprint_completed', op: 'eq', value: true }],
      },
    },
    {
      id: 'are-you-on-track',
      label: assessmentCoverage.areYouOnTrack,
      count: completions.are_you_on_track.count,
      pct: completions.are_you_on_track.percentage,
      segment: {
        label: assessmentCoverage.areYouOnTrack,
        where: [{ field: 'ayot_completed', op: 'eq', value: true }],
      },
    },
    {
      id: 'life-readiness',
      label: assessmentCoverage.lifeReadiness,
      count: completions.life_readiness.count,
      pct: completions.life_readiness.percentage,
      segment: {
        label: assessmentCoverage.lifeReadiness,
        where: [{ field: 'life_readiness_status', op: 'eq', value: 'completed' }],
      },
    },
  ];
};

/** The overview cards keyed by the area names the layouts place them under. */
export const overviewCards = (
  overview: OverviewResponse,
  onCardClick?: (section: AnalyticsSection) => void,
): Record<string, ReactNode> => ({
  students: (
    <StudentsBreakdownCard
      overview={overview}
      onClick={onCardClick && (() => onCardClick('overview'))}
    />
  ),
  engagement: (
    <EngagementMetricCard
      overview={overview}
      onClick={onCardClick && (() => onCardClick('engagement'))}
    />
  ),
  coaches: overview.coaches && (
    <CoachesMetricCard
      counts={overview.coaches}
      onClick={onCardClick && (() => onCardClick('coaching'))}
    />
  ),
  coverage: (
    <AssessmentCoverageCard
      rows={coverageRowsOf(overview)}
      onClick={onCardClick && (() => onCardClick('overview'))}
    />
  ),
});

export const AnalyticsOverviewGrid = ({
  overview,
  onCardClick,
}: Readonly<{
  overview: OverviewResponse;
  onCardClick?: (section: AnalyticsSection) => void;
}>) => {
  useAgentContext(overview);

  const layout = overview.coaches ? OVERVIEW_WITH_COACHES : OVERVIEW_WITHOUT_COACHES;

  return <AnalyticsCardGrid layout={layout} cards={overviewCards(overview, onCardClick)} />;
};

/** A coach's Coaching section: My students alone on Overview; with coverage and activity in detail. */
export const MyCoachingSection = ({
  overview,
  action,
  detail = false,
}: Readonly<{ overview: OverviewResponse; action?: ReactNode; detail?: boolean }>) => {
  const { myCoaching } = coachingAnalytics;

  useMyCoachingAgentContext(overview, detail);

  const cards: Record<string, ReactNode> = detail
    ? {
        students: <MyStudentsCard overview={overview} />,
        coverage: <AssessmentCoverageCard rows={coverageRowsOf(overview)} />,
        activity: overview.my_activity && <MyActivityCard monthly={overview.my_activity.monthly} />,
      }
    : { students: <MyStudentsCard overview={overview} /> };

  return (
    <Box sx={sectionSx}>
      <PageHeader
        title={myCoaching.title}
        subtitle={myCoaching.subtitle}
        maxWidth={null}
        rightComponents={[
          action ?? (
            <LubavButton
              key="my-coaching-discuss-lubav"
              id="coaching-analytics-my-coaching-discuss-lubav"
              label={myCoaching.discussWithLubav}
              promptButtonId="coaching_analytics_my_coaching"
            />
          ),
        ]}
      />
      <AnalyticsCardGrid layout={detail ? COACHING_COACH : MY_STUDENTS_ONLY} cards={cards} />
    </Box>
  );
};

const BreakdownList = ({ rows }: Readonly<{ rows: readonly BreakdownRow[] }>) => (
  <Box sx={rowListSx}>
    {rows.map((row) => (
      <Box key={row.id} id={`coaching-analytics-breakdown-${row.id}`} sx={breakdownRowSx}>
        <Typography variant="text-md-medium" sx={rowLabelSx}>
          {row.label}
        </Typography>
        <CountPill value={row.value} />
      </Box>
    ))}
  </Box>
);

const StudentsBreakdownCard = ({
  overview,
  onClick,
}: Readonly<{ overview: OverviewResponse; onClick?: () => void }>) => {
  const { breakdown, metrics } = coachingAnalytics;
  const [tabIndex, setTabIndex] = useState(0);

  const source = tabIndex === 0 ? overview.students.by_stage : overview.students.by_program;
  const labelOf = tabIndex === 0 ? stageLabel : programLabel;
  const rows: readonly BreakdownRow[] = source.map((bucket) => ({
    id: bucket.id,
    label: labelOf(bucket.id),
    value: bucket.count,
  }));

  return (
    <Box
      id="coaching-analytics-card-students"
      onClick={onClick}
      sx={withClickable(tallCardSx, onClick)}
    >
      <Box sx={studentsHeaderSx}>
        <Box sx={cardHeaderSx}>
          <Typography variant="text-sm-medium" sx={mutedTextSx}>
            {metrics.students}
          </Typography>
          <CardInfoIcon info={[metrics.studentsInfo]} title={metrics.students} />
        </Box>
        <Box sx={studentsValueRowSx}>
          <Typography variant="display-sm-semibold" sx={titleSx}>
            {overview.students.total.toLocaleString()}
          </Typography>
        </Box>
      </Box>
      <Box sx={dividerSx} />
      <Box onClick={(event) => event.stopPropagation()}>
        <HorizontalMenuTabs
          id="coaching-analytics-breakdown-mode"
          variant="filter"
          tabs={[breakdown.byEnrollment, breakdown.byProgram]}
          value={tabIndex}
          setValue={setTabIndex}
          containerSxOverride={breakdownTabsSx}
        />
      </Box>
      <BreakdownList rows={rows} />
    </Box>
  );
};

const MY_STUDENTS_COLUMNS = [...COMMON_COLUMNS, COLUMNS.coach];

/** A coach's own students as vertical bars, by grade or by enrollment stage. */
const MyStudentsCard = ({ overview }: Readonly<{ overview: OverviewResponse }>) => {
  const { myStudents } = coachingAnalytics;
  const [tabIndex, setTabIndex] = useState(0);
  const students = useStudentData(
    'coaching-analytics-card-my-students',
    myStudents.title,
    MY_STUDENTS_COLUMNS,
  );
  const { total } = overview.students;

  const barOf = (
    id: string,
    label: string,
    count: number,
    where: SegmentFilter['where'],
  ): VerticalBarChartBar => ({
    id: `coaching-analytics-my-students-${id}`,
    label,
    value: count,
    tooltip: hover.share(label, count, percentOf(count, total)),
    onClick: () => students.openSegment({ label, where }),
  });

  const bars: readonly VerticalBarChartBar[] =
    tabIndex === 0
      ? overview.students.by_grade.map((bucket) =>
          bucket.id === NO_DATA_FILTER_VALUE
            ? barOf(bucket.id, myStudents.noGrade, bucket.count, [
                { field: 'grade', op: 'is_null' },
              ])
            : barOf(bucket.id, bucket.name ?? bucket.id, bucket.count, [
                { field: 'grade', op: 'eq', value: bucket.id },
              ]),
        )
      : overview.students.by_stage
          .filter((bucket) => bucket.count > 0)
          .map((bucket) =>
            barOf(bucket.id, stageLabel(bucket.id), bucket.count, [
              { field: 'stage', op: 'eq', value: bucket.id },
            ]),
          );

  return (
    <Box id="coaching-analytics-card-my-students" sx={tallCardSx}>
      <Box sx={studentsHeaderSx}>
        <Box sx={cardHeaderSx}>
          <Box sx={titleRowSx}>
            <Typography variant="text-lg-semibold" sx={titleSx}>
              {myStudents.title}
            </Typography>
            <CardInfoIcon info={[myStudents.info]} title={myStudents.title} />
          </Box>
          <ViewDataTrigger handle={students} />
        </Box>
        <Box sx={studentsValueRowSx}>
          <Typography variant="display-sm-semibold" sx={titleSx}>
            {total.toLocaleString()}
          </Typography>
        </Box>
      </Box>
      <Box sx={dividerSx} />
      <HorizontalMenuTabs
        id="coaching-analytics-my-students-mode"
        variant="filter"
        tabs={[myStudents.byGrade, myStudents.byEnrollment]}
        value={tabIndex}
        setValue={setTabIndex}
        containerSxOverride={breakdownTabsSx}
      />
      <VerticalBarChart bars={bars} ariaLabel={myStudents.title} outlineColor="Colors/Base/Black" />
    </Box>
  );
};

const MY_ACTIVITY_SERIES: readonly ActivityLine[] = [
  { key: 'posts', label: coachingAnalytics.coaching.coachActivity.posts, color: 'Brand/800' },
  { key: 'comments', label: coachingAnalytics.coaching.coachActivity.comments, color: 'Brand/300' },
];

/** The viewer's own posts and comments by month. */
const MyActivityCard = ({ monthly }: Readonly<{ monthly: ActivitySeries }>) => {
  const { myActivity, coaching } = coachingAnalytics;
  const months = monthly.series;

  return (
    <AnalyticsCard
      title={myActivity.title}
      subtitle={myActivity.subtitle(months.length)}
      info={[myActivity.info]}
      id="coaching-analytics-card-my-activity"
      belowHeader={<ActivityLegend lines={MY_ACTIVITY_SERIES} />}
    >
      <ActivityLineChart
        months={months}
        lines={MY_ACTIVITY_SERIES}
        ariaLabel={myActivity.title}
        hoverText={(month, entry) =>
          coaching.coachActivity.month(month, entry.posts, entry.comments)
        }
      />
    </AnalyticsCard>
  );
};

const COVERAGE_FILL_COLORS: readonly ColorToken[] = ['Brand/200', 'Brand/400', 'Brand/600'];

/** Below this the badge cannot fit inside the fill, so it sits after the bar. */
const BADGE_INSIDE_FROM_PCT = 12;

const CoverageRowItem = ({
  row,
  fillColor,
  onSelect,
}: Readonly<{
  row: CoverageRow;
  fillColor: ColorToken;
  onSelect: (segment: SegmentFilter) => void;
}>) => {
  const isBadgeInside = row.pct >= BADGE_INSIDE_FROM_PCT;

  return (
    <Box id={`coaching-analytics-coverage-${row.id}`} sx={coverageRowSx}>
      <Typography variant="text-sm-medium" sx={rowLabelSx}>
        {row.label}
      </Typography>
      <Tooltip arrow placement="top" title={hover.count(row.label, row.count)}>
        <Box
          id={`coaching-analytics-coverage-${row.id}-bar`}
          sx={coverageTrackSx}
          onClick={(event) => {
            event.stopPropagation();
            onSelect(row.segment);
          }}
        >
          <Box
            sx={(theme) => ({
              ...coverageFillSx(),
              backgroundColor: theme.backrs.colors[fillColor],
            })}
            style={{ width: `${row.pct}%` }}
          />
          <Box
            sx={coverageBadgeSx}
            style={{
              left: `${row.pct}%`,
              transform: isBadgeInside ? 'translate(-100%, -50%)' : 'translate(0, -50%)',
            }}
          >
            <Typography variant="text-xs-medium" sx={coverageBadgeTextSx}>
              {`${row.pct}%`}
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
};

const COVERAGE_COLUMNS = [
  ...COMMON_COLUMNS,
  COLUMNS.blueprintCompleted,
  COLUMNS.blueprintCompletedAt,
  COLUMNS.ayotCompleted,
  COLUMNS.ayotCompletedAt,
  COLUMNS.lifeReadinessStatus,
  COLUMNS.lifeReadinessPercent,
];

const AssessmentCoverageCard = ({
  rows,
  onClick,
}: Readonly<{ rows: readonly CoverageRow[]; onClick?: () => void }>) => {
  const { title, info } = coachingAnalytics.assessmentCoverage;
  const students = useStudentData(
    'coaching-analytics-card-assessment-coverage',
    title,
    COVERAGE_COLUMNS,
  );

  return (
    <Box
      id="coaching-analytics-card-assessment-coverage"
      onClick={onClick}
      sx={withClickable(tallCardSx, onClick)}
    >
      <Box sx={cardHeaderSx}>
        <Box sx={titleRowSx}>
          <Typography variant="text-lg-semibold" sx={titleSx}>
            {title}
          </Typography>
          <CardInfoIcon info={[info]} title={title} />
        </Box>
        <ViewDataTrigger handle={students} />
      </Box>
      <Box sx={dividerSx} />
      <Box sx={coverageListSx}>
        {rows.map((row, index) => (
          <CoverageRowItem
            key={row.id}
            row={row}
            fillColor={COVERAGE_FILL_COLORS[Math.min(index, COVERAGE_FILL_COLORS.length - 1)]}
            onSelect={students.openSegment}
          />
        ))}
      </Box>
    </Box>
  );
};

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

const cardHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const studentsHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const studentsValueRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: backrs.spacing['spacing-md'],
});

const rowListSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

const breakdownRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  minHeight: 48,
  padding: `${backrs.spacing['spacing-xl']} ${backrs.spacing['spacing-3xl']} ${backrs.spacing['spacing-xl']} 0`,
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  boxSizing: 'border-box',
});

const breakdownTabsSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  borderRadius: backrs.radius['radius-md'],
  '& .MuiTab-root': {
    zIndex: 1,
  },
  '& .MuiTabs-indicator': {
    zIndex: 0,
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
    boxShadow: backrs.shadows['shadow-xs'],
    borderRadius: backrs.radius['radius-md'],
  },
});

const coverageListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

const coverageRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const coverageTrackSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  cursor: 'pointer',
  width: '100%',
  height: 24,
  borderRadius: backrs.radius['radius-md'],
  backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-secondary-hover']}`,
  boxSizing: 'border-box',
  overflow: 'hidden',
});

const coverageFillSx = () => ({
  position: 'absolute' as const,
  left: 0,
  top: 0,
  bottom: 0,
});

const coverageBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  paddingTop: backrs.spacing['spacing-xxs'],
  paddingBottom: backrs.spacing['spacing-xxs'],
  paddingLeft: backrs.spacing['spacing-sm'],
  paddingRight: backrs.spacing['spacing-md'],
  borderRadius: backrs.radius['radius-sm'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-secondary-hover']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  boxShadow: backrs.shadows['shadow-xs'],
});

const coverageBadgeTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});
